import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Menu,
  MenuProps,
  Tooltip,
  Typography,
  makeStyles,
  withStyles,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CardHeader
} from '@material-ui/core';
import { FixMeLaterApi, Listing } from 'src/types';
import { formatCurrency } from 'src/utils';
import { Product } from 'src/types/product';
import { usePermissions, useProductPartDetailsLogic } from 'src/hooks';
import { AlertDialog, Page } from 'src/components';
import { useStockTransfer } from 'src/hooks/stock-transfer';
import { TransferStockListingDialog } from './ProductTransferListingDialog';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import AllInboxIcon from '@material-ui/icons/AllInbox';

interface Props {
  product: Product;
  className?: string;
  listings?: Listing[];
  onDeleteProduct: (product: FixMeLaterApi) => void;
  onSaveDetailPress: (product: Product) => void;
}

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
    marginTop: 3
  }
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center'
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    color: theme.palette.text.primary
  }
}))(MenuItem);

const useStyles = makeStyles(() => ({
  root: {},
  avatar: {
    height: 100,
    width: 100
  },
  addItemBtn: {
    color: 'white',
    marginBottom: '1em',
    background: '#ff9800',
    '&:hover': {
      backgroundColor: '#ff9800',
      color: 'white'
    },
    '&:active': {
      backgroundColor: '#ff9800',
      color: 'white'
    }
  }
}));

const ProductDetailView = ({
  className,
  listings,
  product,
  ...rest
}: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { resetProductPartsStates } = useProductPartDetailsLogic();
  const {
    transferCount,
    transferCountListing,
    getTransferCount,
    getTransferCountListings
  } = useStockTransfer();
  const {
    viewTransferListingStock,
    canEditProductInformation,
    canAddProductListing
  } = usePermissions();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onAddListingItemPress = useCallback(() => {
    const sampleSerialNos = listings
      ?.map((listing) => listing?.serial_no)
      ?.filter((serialNo) => serialNo)
      ?.splice(0, 3);

    navigate(
      product.is_product_bundle
        ? '/app/products-listings/add?type=bundle'
        : '/app/products-listings/add',
      {
        state: { ...product, sample_serial_nos: sampleSerialNos }
      }
    );
  }, [listings, navigate, product]);

  const onEditProductInformationPress = useCallback(() => {
    navigate(`/app/products/information/${product?.id}`);
  }, [navigate, product.id]);

  const handleTransferStockOpen = useCallback(() => {
    setIsOpen(true);
    getTransferCountListings(product?.id);
  }, [getTransferCountListings, product]);

  const renderProductUtilities = useCallback(() => {
    return (
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems={isMobile ? 'flex-start' : 'flex-end'}
        mt={1}
        flexDirection="column"
      >
        <Button
          aria-controls="product-menu"
          aria-haspopup="true"
          variant="outlined"
          onClick={handleClick}
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {isMobile ? (
            <AllInboxIcon />
          ) : (
            <>
              Product Utilities
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: 3,
                  marginLeft: 4
                }}
              >
                {anchorEl ? (
                  <ExpandLessIcon style={{ fontSize: 20 }} />
                ) : (
                  <ExpandMoreIcon style={{ fontSize: 20 }} />
                )}
              </span>
            </>
          )}
        </Button>

        <StyledMenu
          id="product-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {canAddProductListing && (
            <>
              <Tooltip
                title="Click here to add a listing inside this product"
                arrow
              >
                <StyledMenuItem
                  onClick={() => {
                    onAddListingItemPress();
                    handleClose();
                  }}
                >
                  <ListItemIcon>
                    <AddIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Add an Item" />
                </StyledMenuItem>
              </Tooltip>
            </>
          )}

          {canEditProductInformation && (
            <StyledMenuItem
              onClick={() => {
                onEditProductInformationPress();
                handleClose();
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Edit Product Information" />
            </StyledMenuItem>
          )}

          <StyledMenuItem
            disabled={isEmpty(product)}
            onClick={() => {
              navigate(`/app/flash-deals/add`, { state: product });
              handleClose();
            }}
          >
            <ListItemIcon>
              <FlashOnIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="Add on Flash Deals" />
          </StyledMenuItem>
        </StyledMenu>
      </Box>
    );
  }, [
    anchorEl,
    canAddProductListing,
    canEditProductInformation,
    isMobile,
    navigate,
    onAddListingItemPress,
    onEditProductInformationPress,
    product
  ]);

  useEffect(() => {
    getTransferCount(product?.id);
  }, [getTransferCount, getTransferCountListings, product.id]);

  useEffect(() => {
    return () => {
      resetProductPartsStates();
    };
  }, [resetProductPartsStates]);

  if (product?.is_hidden) {
    return (
      <AlertDialog
        title="Deleted Product"
        isVisible={true}
        subTitle="Information page for the following product is unavailable."
        handleClose={() => navigate('/app/products')}
      />
    );
  }

  return (
    <Page className={classes.root} title={`${product?.name || ''}`}>
      <Box mt={2}>
        <Card className={clsx(classes.root, className)} {...rest}>
          {/*  */}
          <CardHeader
            title={<Typography variant="h2">{product?.name ?? ''}</Typography>}
            subheader={
              <Typography variant="body2">{product?.sku ?? ''}</Typography>
            }
            action={renderProductUtilities()}
          />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item lg={2} md={3} sm={12} xs={12}>
                <Typography color="textPrimary" variant="h5">
                  Price:
                  {product?.retail_price
                    ? ` ${formatCurrency(product?.retail_price)}`
                    : '₱0'}
                </Typography>
              </Grid>

              <Grid item lg={2} md={3} sm={12} xs={12}>
                <Typography color="textPrimary" variant="h5">
                  Category: {product?.category ?? ''}
                </Typography>
              </Grid>

              <Grid item lg={2} md={3} sm={12} xs={12}>
                <Typography color="textPrimary" variant="h5">
                  {`Available Stock(s): ${product?.available_count}`}
                </Typography>
              </Grid>

              <Grid item lg={2} md={3} sm={12} xs={12}>
                <Typography color="textPrimary" variant="h5">
                  hidden from pricelist:{' '}
                  {product?.is_hidden_from_pricelist === 1 ? 'Yes' : 'No'}
                </Typography>
              </Grid>

              <Grid item lg={2} md={3} sm={12} xs={12}>
                <Typography color="textPrimary" variant="h5">
                  hidden from quotation:{' '}
                  {product?.is_hidden_from_quotation === 1 ? 'Yes' : 'No'}
                </Typography>
              </Grid>

              <Grid item lg={2} md={3} sm={12} xs={12}>
                <Typography color="textPrimary" variant="h5">
                  Ecomm Views: {product?.visit_count ?? ''}
                </Typography>
              </Grid>

              <Grid item lg={2} md={3} sm={12} xs={12}>
                {transferCount && viewTransferListingStock ? (
                  <Button
                    variant="outlined"
                    style={{ marginTop: '1rem' }}
                    color="secondary"
                    onClick={handleTransferStockOpen}
                  >
                    {`On Transfer: ${transferCount} Item(s)`}
                  </Button>
                ) : null}

                {/* TODO: guess replace button above with transfer (gettranfercount or something) */}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      <TransferStockListingDialog
        isOpen={isOpen}
        transferListings={transferCountListing}
        onHandleClose={() => setIsOpen(false)}
      />
    </Page>
  );
};

export default ProductDetailView;
